import React, { useState } from "react";
import { flushSync } from "react-dom";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { markdownFormatter } from "./Utils";
import { convertQuotes } from "./lib/convertQuotes";
import classnames from "classnames";
import Icon from "./Icon";
import SVG from "react-inlinesvg";
import ClampLines from "react-clamp-lines";
import { parseContent as parseImageCardContent } from "./cardset/ImageCardView";

function getRelativeCoordinates(event, referenceElement) {
  const position = {
    x: event.pageX,
    y: event.pageY,
  };

  const offset = {
    left: referenceElement.offsetLeft,
    top: referenceElement.offsetTop,
  };

  let reference = referenceElement.offsetParent;

  while (reference) {
    offset.left += reference.offsetLeft;
    offset.top += reference.offsetTop;
    reference = reference.offsetParent;
  }

  return {
    x: position.x - offset.left,
    y: position.y - offset.top,
  };
}

const DragHandle = SortableHandle(() => (
  <span className="drag">
    <SVG src="/assets/handles.svg" />
  </span>
));

const FavoritesList = SortableContainer(({ children }) => {
  return <ol id="favorites">{children}</ol>;
});

const SortableItem = SortableElement(({ value }) => <li>{value}</li>);

function Favorites(props) {
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  let ref = React.createRef();

  // This calculates the coordinates of the
  // mouse cursor relative to the top of the list
  // used to offset so that the sortable element that is going to be dragged
  // remains centered on the cursor
  React.useEffect(() => {
    let el = window.document.getElementById("favorites");

    if (el) {
      const setFromEvent = (e) => {
        let coords = getRelativeCoordinates(e, el);

        setPosition(coords);
      };
      el.addEventListener("mousemove", setFromEvent);

      return () => {
        el.removeEventListener("mousemove", setFromEvent);
      };
    }
  }, []);

  if (props.favorites.length == 0) {
    return (
      <div className="empty">
        <p>You have no favorites yet.</p>
        <p className="instruction">
          Click{" "}
          <SVG
            aria-label="Favorite This, on a card, "
            src="/assets/star.fill.svg"
          />{" "}
          to add it to this list.
        </p>
      </div>
    );
  } else {
    return (
      <FavoritesList
        useDragHandle
        onSortEnd={({ oldIndex, newIndex }) => {
          // react-sortable-hoc fires this from a native mouseup listener, so the
          // reorder must be flushed before the offsetTop read below sees it.
          flushSync(() => {
            setDragging(false);
            props.onFavoriteReorder(oldIndex, newIndex);
          });

          // Scroll to the favorite that was just moved after it has been repositioned
          // TODO: hacky use of DOM manipulation
          let el = window.document.getElementById("favorites");
          let fav = el.children[newIndex];
          el.scrollTop = fav.offsetTop - position.y + 15;
        }}
        updateBeforeSortStart={({ node, index, collection, isKeySorting }) => {
          setDragging(true);

          // Do our best to position the element so that it is still under the mouse cursor
          let top = node.offsetTop;
          let scrollable = node.parentElement;
          scrollable.scrollTop = top - position.y + 15;
        }}
      >
        {props.favorites.map((favorite, index) => {
          return (
            <SortableItem
              key={favorite.id}
              index={index}
              value={
                <Favorite
                  dragging={dragging}
                  onRemove={async (e) => {
                    e.preventDefault();
                    props.onDeleteFavorite(favorite.id);
                  }}
                  prompt={favorite.prompt}
                  path={favorite.path}
                  fid={favorite.id}
                  content={favorite.content}
                  insertEditor={props.insertEditor}
                  source={favorite.source}
                  editor={props.editor}
                />
              }
            />
          );
        })}
        {dragging && <div className="spacer"></div>}
      </FavoritesList>
    );
  }
}

function Favorite(props) {
  const [collapsed, setCollapsed] = useState(false);

  // formats a completion to look nice
  let format = (text) => {
    let out = text;

    // replace double newlines and tabs with just one
    //out = out.replace(/\n\s*\n/g, '\n');
    out = out.replace(/\t\s*\t/g, "\t");
    out = out.replace(/\t/g, "\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0");

    return out;
  };

  let prompt = props.prompt ?? "";

  const truncateLength = 50;

  if (prompt.length > truncateLength) {
    prompt = prompt.slice(prompt.length - truncateLength, prompt.length);
    prompt = "... " + prompt;
  }

  let sourceImage;
  switch (props.source) {
    case "rewrite":
    case "brainstorm":
    case "plugin":
    case "command":
    case "quickEditRestore":
    case "myVoiceRewrite":
    case "multiverse":
    case "guidedWormhole":
      sourceImage = (
        <Icon
          left={5}
          width={20}
          source={props.source}
        />
      );
      break;
    case "twist":
      sourceImage = (
        <Icon
          left={3}
          width={25}
          source={props.source}
        />
      );
      break;
    case "character":
      sourceImage = (
        <Icon
          width={30}
          source={props.source}
        />
      );
      break;
    case "poem":
      sourceImage = (
        <Icon
          width={30}
          source={props.source}
        />
      );
      break;
    case "visualize":
      sourceImage = (
        <Icon
          width={30}
          source={props.source}
        />
      );
      break;
    case "description":
      switch (props.path[0]) {
        case "metaphor":
          sourceImage = (
            <SVG
              height={25}
              style={{ width: "30px" }}
              src="/assets/noun_Rose_1282645.svg"
            />
          );
          break;
        case "sight":
          sourceImage = (
            <SVG
              height={30}
              style={{ width: "30px" }}
              src="/assets/noun_eyes_MODIFIED_2743436.svg"
            />
          );
          break;
        case "smell":
          sourceImage = (
            <SVG
              height={25}
              style={{ width: "28px" }}
              src="/assets/noun_smell_20407.svg"
            />
          );
          break;
        case "sound":
          sourceImage = (
            <SVG
              height={25}
              style={{ width: "32px" }}
              src="/assets/noun_Listen_21603.svg"
            />
          );
          break;
        case "taste":
          sourceImage = (
            <SVG
              height={25}
              style={{ width: "26px" }}
              src="/assets/noun_taste_2173029.svg"
            />
          );
          break;
        case "touch":
          sourceImage = (
            <SVG
              height={25}
              style={{ width: "32px" }}
              src="/assets/noun_touch_MODIFIED_181230.svg"
            />
          );
          break;
      }

      break;
    case "whatIf":
      sourceImage = <span>🤔</span>;
      break;
    case "expand":
      sourceImage = <span>💥</span>;
      break;
    case "summary":
    case "shrinkRay":
      sourceImage = <span>📝</span>;
      break;
    case "analyze":
      sourceImage = <span>🤓</span>;
      break;
    default:
      sourceImage = null;
      break;
  }

  let contentForDisplay = props.content.trim();
  if (props.editor) {
    contentForDisplay = convertQuotes(contentForDisplay, props.editor);
  }
  let expandedContent = markdownFormatter(format(contentForDisplay));
  if (props.source === "visualize") {
    const imgSrc = parseImageCardContent(props.content)[1];
    expandedContent = (
      <img
        src={imgSrc}
        style={{ width: "100%", objectFit: "cover" }}
        alt="generation"
      />
    );
  }

  return (
    <div
      className={classnames({ favorite: true, dragging: props.dragging })}
      key={props.fid}
    >
      <div className="title">
        <div className="expand">
          {!collapsed && (
            <a
              href="#"
              style={{ top: "-4px" }}
              onClick={(e) => {
                e.preventDefault();
                setCollapsed(true);
              }}
            >
              <SVG src="/assets/favorite-collapse.svg" />
            </a>
          )}
          {collapsed && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setCollapsed(false);
              }}
            >
              <SVG src="/assets/favorite-expand.svg" />
            </a>
          )}
        </div>
        <DragHandle />
        <div className="menu">
          {/* <a href="#" onClick={(e) => {
          e.preventDefault();
          props.insertEditor(props.content);
        }}>
          <i className="fas fa-arrow-left"></i>
        </a>

        <CopyToClipboard text={props.content}
          onCopy={() => {}}>
          <a>
            <i className="far fa-copy"></i>
          </a>
        </CopyToClipboard> */}

          <a
            href="#"
            className="unstar"
            onClick={props.onRemove}
          >
            <SVG src="/assets/star.fill.svg" />
          </a>
        </div>
        {/* From {SOURCE_DISPLAY[props.source]} */}
      </div>

      <div className="content">
        {collapsed && (
          <ClampLines
            text={props.content}
            lines={2}
          />
        )}
        {!collapsed && <>{expandedContent} </>}
      </div>

      <div className="footer">
        <div className="source">{sourceImage}</div>
        <div className="prompt">{prompt}</div>
        <div className="clearfix"></div>
      </div>
    </div>
  );
}

export default Favorites;
